import * as XLSX from 'xlsx';

/**
 * Parse a CD / album spreadsheet (.xls or .xlsx).
 *
 * Two supported sheet layouts:
 *   - 'basic'    → 13 columns: Track #, Song Title, Composer, Publishing,
 *                  Artist, Catalogue #, Album Title, UPC, ISRC, Time,
 *                  Release Date, Vocal/Instrumental, NYV.
 *   - 'expanded' → 31 columns: all of the basic columns (minus NYV) plus
 *                  Executive Producer, Producer, Associate Producer,
 *                  Recording Engineer, Editing Engineer, Mixing Engineer,
 *                  Mastering Engineer, Assistants, Musicians, Recording
 *                  Dates, Recording Location, Cover Art Design, Package
 *                  Design, Photography, Liner Notes Author, Liner Notes
 *                  Text, Sponsor Credit, Awards, Link to Product.
 *
 * Both formats have 3 header rows before data, and may contain repeated
 * "Song Title" sub-headers that must be dropped.
 *
 * Returns:
 *   {
 *     format: 'basic' | 'expanded',
 *     albums: [ { _idx, title, catalogue_num, upc, title_artist,
 *                 release_date, ...production fields..., tracks: [...] }, ... ],
 *     counts: { albums: N, tracks: N }
 *   }
 */

const BASIC_COLUMNS = [
  'track_num', 'song_title', 'composer', 'publishing', 'artist',
  'catalogue_num', 'album_title', 'upc', 'isrc', 'time',
  'release_date', 'vocal_instrumental', 'nyv',
];

const EXPANDED_COLUMNS = [
  'track_num', 'song_title', 'composer', 'publishing', 'artist',
  'catalogue_num', 'album_title', 'upc', 'isrc', 'time',
  'release_date', 'vocal_instrumental',
  'executive_producer', 'producer', 'associate_producer',
  'recording_engineer', 'editing_engineer', 'mixing_engineer',
  'mastering_engineer', 'assistants', 'musicians',
  'recording_dates', 'recording_location',
  'cover_art', 'cover_art_design', 'package_design', 'photography',
  'liner_notes_author', 'liner_notes_text', 'sponsor_credit',
  'awards', 'link_to_product',
];

const ALBUM_LEVEL_FIELDS = [
  'executive_producer', 'producer', 'associate_producer',
  'recording_engineer', 'editing_engineer', 'mixing_engineer',
  'mastering_engineer', 'assistants',
  'recording_dates', 'recording_location',
  'cover_art', 'cover_art_design', 'package_design', 'photography',
  'liner_notes_author', 'liner_notes_text', 'sponsor_credit',
  'awards', 'link_to_product',
];

/**
 * Header-string aliases for each internal field. We resolve column
 * positions by matching the spreadsheet's header row against this map
 * (lowercased + trimmed) instead of relying on fixed positions, so a new
 * column inserted in the middle (e.g. a "Cover Art" link column appearing
 * before "Cover Art Design") doesn't silently shift every downstream
 * field by one and corrupt the data.
 */
const COLUMN_HEADER_ALIASES = {
  track_num: ['track #', 'track number', 'track'],
  song_title: ['song title'],
  composer: ['composer'],
  publishing: ['publishing'],
  artist: ['title artist', 'artist'],
  catalogue_num: ['catalogue #', 'catalog #', 'catalogue number', 'catalog number'],
  album_title: ['album title'],
  upc: ['upc'],
  isrc: ['isrc'],
  time: ['time', 'duration'],
  release_date: ['release date'],
  vocal_instrumental: ['vocal/instrumental', 'vocal instrumental'],
  nyv: ['nyv'],
  executive_producer: ['executive producer'],
  producer: ['producer'],
  associate_producer: ['associate producer'],
  recording_engineer: ['recording engineer'],
  editing_engineer: ['editing engineer'],
  mixing_engineer: ['mixing engineer'],
  mastering_engineer: ['mastering engineer'],
  assistants: ['assistants'],
  musicians: ['musicians'],
  recording_dates: ['recording dates'],
  recording_location: ['recording location'],
  cover_art: ['cover art', 'cover art link', 'cover art url'],
  cover_art_design: ['cover art design'],
  package_design: ['package design'],
  photography: ['photography'],
  liner_notes_author: ['liner notes author'],
  liner_notes_text: ['liner notes text'],
  sponsor_credit: ['sponsor credit'],
  awards: ['awards'],
  link_to_product: ['link to product'],
};

export function parseCdUploadFile(file, format) {
  if (format !== 'basic' && format !== 'expanded') {
    return Promise.reject(
      new Error(`Unknown CD spreadsheet format: "${format}".`),
    );
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          reject(new Error('The spreadsheet is empty.'));
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const fields = format === 'basic' ? BASIC_COLUMNS : EXPANDED_COLUMNS;
        const headerInfo = detectHeaderColumns(sheet, fields);

        if (!headerInfo) {
          reject(
            new Error(
              'Could not find a header row containing "Song Title". Make sure the spreadsheet uses the standard column headers.',
            ),
          );
          return;
        }

        const missingRequired = ['song_title', 'album_title'].filter(
          (f) => !(f in headerInfo.columnMap),
        );
        if (missingRequired.length > 0) {
          reject(
            new Error(
              `Spreadsheet is missing required column${missingRequired.length === 1 ? '' : 's'}: ${missingRequired.map((f) => COLUMN_HEADER_ALIASES[f][0]).join(', ')}. Double-check the format selector matches the file.`,
            ),
          );
          return;
        }

        const linkColumns = new Set();
        for (const f of ['link_to_product', 'cover_art']) {
          const idx = headerInfo.columnMap[f];
          if (typeof idx === 'number') linkColumns.add(idx);
        }
        const rows = extractSheetRows(sheet, linkColumns);

        if (rows.length === 0) {
          reject(new Error('The first sheet contains no rows.'));
          return;
        }

        const albums = buildAlbums(rows, headerInfo.columnMap, fields, format);

        if (albums.length === 0) {
          reject(
            new Error(
              'No album rows were found. Double-check the spreadsheet format selector above matches the file you uploaded.',
            ),
          );
          return;
        }

        const trackCount = albums.reduce((n, a) => n + a.tracks.length, 0);
        resolve({
          format,
          albums,
          counts: { albums: albums.length, tracks: trackCount },
        });
      } catch (err) {
        reject(new Error(`Failed to parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsArrayBuffer(file);
  });
}

function buildAlbums(rows, columnMap, fields, format) {
  const byKey = new Map();
  const artistsByKey = new Map();
  const order = [];

  rows.forEach((row) => {
    const record = rowToRecord(row, columnMap, fields);
    if (!isDataRow(record)) return;

    const title = record.album_title;
    const cat = record.catalogue_num;
    const key = cat || normalizeTitle(title);
    if (!key || !title) return;

    if (!byKey.has(key)) {
      const album = {
        _idx: order.length,
        title,
        catalogue_num: cat || null,
        upc: record.upc || null,
        title_artist: record.artist || null,
        release_date: record.release_date || null,
        tracks: [],
      };
      if (format === 'expanded') {
        ALBUM_LEVEL_FIELDS.forEach((field) => {
          album[field] = record[field] || null;
        });
      }
      byKey.set(key, album);
      artistsByKey.set(key, []);
      order.push(key);
    } else if (format === 'expanded') {
      const album = byKey.get(key);
      ALBUM_LEVEL_FIELDS.forEach((field) => {
        if (!album[field] && record[field]) {
          album[field] = record[field];
        }
      });
    }

    const album = byKey.get(key);
    const trackArtist = record.artist || null;
    if (trackArtist) artistsByKey.get(key).push(trackArtist);

    const trackMusicians =
      format === 'expanded' ? record.musicians || trackArtist : trackArtist;

    album.tracks.push({
      _idx: album.tracks.length,
      track_number: parseTrackNumber(record.track_num),
      song_title: record.song_title || null,
      composer: record.composer || null,
      publishing: record.publishing || null,
      isrc: record.isrc || null,
      duration: parseDuration(record.time),
      vocal_instrumental: record.vocal_instrumental || null,
      musicians: trackMusicians || null,
    });
  });

  order.forEach((key) => {
    const album = byKey.get(key);
    const artists = artistsByKey.get(key) || [];
    const unique = [];
    const seen = new Set();
    artists.forEach((a) => {
      if (a && !seen.has(a)) {
        seen.add(a);
        unique.push(a);
      }
    });
    if (unique.length > 3) {
      album.title_artist = 'Various Artists';
    } else if (unique.length > 1) {
      album.title_artist = unique.join(' / ');
    } else if (unique.length === 1 && !album.title_artist) {
      album.title_artist = unique[0];
    }
    album.tracks.sort((a, b) => {
      const an = a.track_number ?? Number.MAX_SAFE_INTEGER;
      const bn = b.track_number ?? Number.MAX_SAFE_INTEGER;
      return an - bn;
    });
    album.tracks.forEach((t, i) => {
      t._idx = i;
    });
  });

  return order.map((key) => byKey.get(key));
}

/**
 * Walk the sheet cell-by-cell instead of using sheet_to_json, so we can
 * reach each cell's hyperlink target when one exists. For the
 * `link_to_product` column MCG often puts a human-readable label
 * ("MCG Jazz — Album Name") with the actual product URL attached as an
 * Excel hyperlink; sheet_to_json would return the label and drop the URL.
 * Prefer the hyperlink target there; everything else keeps the raw value
 * (Date, number, or string) so downstream formatters can handle it.
 * Empty rows are filtered out so downstream callers don't need to.
 */
function extractSheetRows(sheet, linkColumns) {
  const ref = sheet['!ref'];
  if (!ref) return [];
  const range = XLSX.utils.decode_range(ref);
  const out = [];
  for (let r = range.s.r; r <= range.e.r; r += 1) {
    const row = [];
    let hasAny = false;
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr];
      let value = null;
      if (cell) {
        if (linkColumns.has(c) && cell.l && cell.l.Target) {
          // SheetJS hands back the raw XML attribute, so `&` shows up as
          // `&amp;`. Decode the five predefined XML entities before we
          // store the URL — otherwise query strings with `&amp;` will
          // 404 when the link is opened.
          value = decodeXmlEntities(cell.l.Target);
        } else if (cell.v !== undefined) {
          value = cell.v;
        }
      }
      row.push(value);
      if (value !== null && value !== '') hasAny = true;
    }
    if (hasAny) out.push(row);
  }
  return out;
}

/**
 * Locate the column-header row by scanning the top of the sheet for a
 * cell whose value is "Song Title" (case-insensitive). Returns the row
 * index plus a field → column-index map built from the header aliases.
 * Header-driven so an inserted column (like the new "Cover Art" link
 * next to "Cover Art Design") doesn't shift every downstream field.
 */
function detectHeaderColumns(sheet, fields) {
  const ref = sheet['!ref'];
  if (!ref) return null;
  const range = XLSX.utils.decode_range(ref);
  const limit = Math.min(range.e.r, range.s.r + 19);
  for (let r = range.s.r; r <= limit; r += 1) {
    const headers = [];
    let foundSongTitle = false;
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      const v = cell && cell.v != null ? cell.v : null;
      const norm = typeof v === 'string' ? v.trim().toLowerCase() : null;
      headers.push(norm);
      if (norm === 'song title') foundSongTitle = true;
    }
    if (!foundSongTitle) continue;

    const columnMap = {};
    for (const field of fields) {
      const aliases = COLUMN_HEADER_ALIASES[field] || [field];
      const idx = headers.findIndex((h) => h && aliases.includes(h));
      if (idx >= 0) columnMap[field] = idx;
    }
    return { headerRowIdx: r, columnMap };
  }
  return null;
}

function rowToRecord(row, columnMap, fields) {
  const record = {};
  for (const field of fields) {
    record[field] = null;
  }
  for (const field of Object.keys(columnMap)) {
    const i = columnMap[field];
    const raw = row[i];
    // Duration is a time-of-day cell; keep the raw value (Date, number, or
    // string) so parseDuration can format it. All other columns get
    // coerced to strings here.
    if (field === 'time') {
      record[field] = raw ?? null;
    } else {
      record[field] = safeString(raw);
    }
  }
  return record;
}

function isDataRow(record) {
  const title = record.song_title;
  if (!title) return false;
  if (title.toLowerCase() === 'song title') return false;
  return true;
}

function safeString(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === 'number') {
    if (!Number.isFinite(val)) return null;
    return String(val).trim() || null;
  }
  if (val instanceof Date) {
    if (Number.isNaN(val.getTime())) return null;
    return formatDate(val);
  }
  const s = String(val).trim();
  return s.length > 0 ? s : null;
}

function formatDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Track numbers come through as ints, "Disc N track M" strings, or
 * free-text with a trailing number. Multi-disc tracks are encoded as
 * disc * 100 + track to preserve ordering.
 */
function parseTrackNumber(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === 'number' && Number.isFinite(val)) {
    return Math.trunc(val);
  }
  const s = String(val).trim();
  if (!s) return null;
  const disc = s.match(/Disc\s+(\d+)\s+track\s+(\d+)/i);
  if (disc) {
    return Number(disc[1]) * 100 + Number(disc[2]);
  }
  const tail = s.match(/(\d+)\s*$/);
  if (tail) return Number(tail[1]);
  return null;
}

/**
 * Durations may come as:
 *   - a Date (SheetJS with cellDates: true converts Excel time cells)
 *   - an Excel serial number (fractional days)
 *   - a string like "4:23" or "12:05:00"
 * Returns MM:SS, dropping hours when zero.
 */
function parseDuration(val) {
  if (val === null || val === undefined) return null;

  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    // SheetJS builds time-of-day cells in the local timezone, so the
    // *local* H/M/S match what the user sees in Excel. Using the UTC
    // accessors here would shift durations by the user's offset.
    const totalSec =
      val.getHours() * 3600 + val.getMinutes() * 60 + val.getSeconds();
    return secondsToClock(totalSec);
  }

  if (typeof val === 'number' && Number.isFinite(val)) {
    const totalSec = Math.round(val * 24 * 3600);
    return secondsToClock(totalSec);
  }

  const s = String(val).trim();
  if (!s) return null;
  const parts = s.split(':').map((p) => p.trim());
  if (parts.length >= 2 && parts.every((p) => /^\d+$/.test(p))) {
    return s;
  }
  return s;
}

function secondsToClock(total) {
  if (total <= 0) return null;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function decodeXmlEntities(s) {
  return String(s)
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function normalizeTitle(title) {
  if (!title) return '';
  return String(title).toLowerCase().replace(/[^a-z0-9]/g, '');
}
